/**
 * FormEditor - 表單編輯器（簡化版）
 * 
 * 設計原則：
 * 1. 前端只負責 UI 顯示和用戶交互
 * 2. 所有邏輯驗證由後端處理
 * 3. 審批流程由後端計算，前端只顯示
 * 4. locale 在 config API 中一起返回，不拆分載入
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import { useToast } from '../../context/ToastContext';
import { 
    useFormConfig,
    useFormDetail,
    useCreateForm,
    useUpdateForm,
    useDeleteForm,
    useSubmitForm,
    useCalculateForm
} from '../../../hooks/useApi';
import DynamicFormRenderer from '../../components/DynamicFormRenderer';
import { ApproverFlowEditor } from '../../components/ApprovalFlow';
import AdminPanel from '../../components/AdminPanel';

// 導入共享模組以支援前端直接計算
import { 
    executeCalculations,
    getCalculationTargets 
} from '@/lib/shared/formula-evaluator.js';

import { 
    shouldSkipStep 
} from '@/lib/shared/condition-evaluator.js';

const FormEditorNew = () => {
    const { formId, applicationId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t, getText, language } = useI18n(['common', 'forms']);
    const toast = useToast();

    // 表單狀態
    const [formData, setFormData] = useState({});
    const [approvalFlow, setApprovalFlow] = useState([]);
    const [errors, setErrors] = useState({});
    
    // 判斷是否為編輯模式, true 表示編輯已存在的表單, false 表示新建表單
    const isEditMode = !!applicationId;

    // 取得當前 formId
    const { data: formResult, isLoading: formLoading } = useFormDetail(applicationId);
    const existingFormId = formResult?.data?.formId;
    const currentFormId = isEditMode ? existingFormId : formId;

    // 載入 config（包含 locale）- 後端會一起返回
    const { data: configResult, isLoading: configLoading } = useFormConfig(currentFormId, language);

    // Mutations
    const createForm = useCreateForm();
    const updateForm = useUpdateForm();
    const deleteForm = useDeleteForm();
    const submitForm = useSubmitForm();
    const calculateForm = useCalculateForm();

    // 選擇正確的配置
    const config = configResult?.data;
    const formConfig = config?.form;
    const routeConfig = config?.route;
    const previewFlow = config?.previewFlow;

    // 管理員權限檢查
    const isAdmin = user?.admin || user?.roles?.includes('admin');
    
    // 表單狀態和編輯權限
    const formStatus = formResult?.data?.status || 'draft';
    // Admin 可以編輯任何狀態的表單
    const canEdit = isAdmin || formStatus === 'draft' || formStatus === 'returned';

    // 初始化表單數據
    useEffect(() => {
        if (isEditMode && formResult?.data) {
            // 編輯模式：使用已存數據
            setFormData(formResult.data.formData || {});
            setApprovalFlow(formResult.data.approvalFlow || []);
        } else if (!isEditMode && previewFlow && user) {
            // 新建模式：使用預覽流程
            setApprovalFlow(previewFlow);
            
            // 初始化默認值
            const initial = {};
            Object.entries(formConfig?.fields || {}).forEach(([fieldId, field]) => {
                // 處理 source 自動填充
                if (field.source && user) {
                    const path = field.source.split('.');
                    if (path[0] === 'user') {
                        let value = user;
                        for (let i = 1; i < path.length; i++) {
                            value = value?.[path[i]];
                        }
                        if (value) initial[fieldId] = value;
                    }
                }
                // 處理默認值
                if (field.defaultValue && !initial[fieldId]) {
                    if (field.defaultValue === 'today') {
                        initial[fieldId] = new Date().toISOString().split('T')[0];
                    } else {
                        initial[fieldId] = field.defaultValue;
                    }
                }
            });
            setFormData(initial);
            
            // 初始化後觸發一次計算，以正確評估條件和跳過狀態
            if (currentFormId) {
                calculateForm.mutateAsync({
                    formId: currentFormId,
                    formData: initial,
                    fieldId: null,
                    calculateFlow: true,
                }).then(result => {
                    if (result?.data?.approvalFlow) {
                        setApprovalFlow(result.data.approvalFlow);
                    }
                }).catch(err => {
                    console.warn('Initial calculation warning:', err.message);
                });
            }
        }
    }, [isEditMode, formResult, formConfig, previewFlow, user, currentFormId]);

    // 表單變更處理 - 前端直接計算公式，只在需要時調用後端計算審批流程
    const handleChange = useCallback(async (newData, changedFieldId) => {
        setFormData(newData);
        setErrors({});

        // 如果有變更的欄位，進行計算
        if (changedFieldId && currentFormId) {
            try {
                const calculations = formConfig?.calculations || {};
                
                // 🔥 前端直接計算公式（使用共享模組）
                const { updatedFormData, changedFields } = executeCalculations(
                    calculations, 
                    newData, 
                    changedFieldId
                );
                
                // 如果有計算結果，更新表單數據
                if (changedFields.length > 0) {
                    setFormData(updatedFormData);
                    newData = updatedFormData; // 更新引用供後續使用
                }
                
                // 找出所有計算目標欄位
                const calculatedTargets = getCalculationTargets(calculations, changedFieldId);

                // 收集所有條件欄位（用於流程計算）
                const conditionFields = new Set();
                routeConfig?.steps?.forEach(step => {
                    step.conditions?.forEach(c => conditionFields.add(c.field));
                    step.skipConditions?.forEach(c => conditionFields.add(c.field));
                });
                
                // 檢查是否需要重新計算審批流程：
                // 1. 直接變更的欄位是條件欄位
                // 2. 或者計算結果欄位是條件欄位
                const shouldCalculateFlow = conditionFields.has(changedFieldId) || 
                    calculatedTargets.some(target => conditionFields.has(target));

                // 如果不需要計算審批流程，直接返回
                if (!shouldCalculateFlow) {
                    return;
                }

                // 🔥 前端先預覽審批流程的跳過狀態（使用共享模組）
                const previewFlow = routeConfig?.steps?.map(step => {
                    const { isSkipped, skipReason } = shouldSkipStep(step, newData);
                    return {
                        stepId: step.id,
                        stepName: step.name,
                        isSkipped,
                        skipReason,
                    };
                });

                // 檢查跳過狀態是否改變
                const skipStatusChanged = previewFlow?.some((preview, index) => {
                    const existing = approvalFlow[index];
                    return existing && existing.isSkipped !== preview.isSkipped;
                });

                // 只有在跳過狀態改變時才調用後端（需要重新解析審批人）
                if (!skipStatusChanged) {
                    return;
                }

                // 調用後端計算審批流程（需要解析審批人）
                const result = await calculateForm.mutateAsync({
                    formId: currentFormId,
                    formData: newData,
                    fieldId: changedFieldId,
                    calculateFlow: true,
                    existingFlow: approvalFlow,
                });

                if (result?.data) {
                    // 注意：formData 已經在前端計算過了，這裡不需要再更新
                    // 只需要更新審批流程
                    if (result.data.approvalFlow) {
                        const mergedFlow = result.data.approvalFlow.map(newStep => {
                            const existingStep = approvalFlow.find(s => s.stepId === newStep.stepId);
                            if (existingStep?.isOverridden || existingStep?.isAdded) {
                                // 保留用戶修改的審批人，但更新跳過狀態
                                return {
                                    ...newStep,
                                    approvers: existingStep.approvers,
                                    approvalMode: existingStep.approvalMode,
                                    isOverridden: existingStep.isOverridden,
                                    isAdded: existingStep.isAdded,
                                };
                            }
                            return newStep;
                        });
                        // 保留用戶新增的步驟
                        const addedSteps = approvalFlow.filter(s => s.isAdded && !result.data.approvalFlow.find(n => n.stepId === s.stepId));
                        setApprovalFlow([...mergedFlow, ...addedSteps]);
                    }
                }
            } catch (err) {
                console.error('Calculate error:', err);
                // 計算失敗不影響用戶輸入
            }
        }
    }, [currentFormId, calculateForm, approvalFlow, formConfig, routeConfig]);

    // 保存草稿
    const handleSave = async () => {
        try {
            if (isEditMode) {
                await updateForm.mutateAsync({
                    applicationId,
                    data: { formData, approvalFlow }
                });
                toast.success(t('forms:draftSaved'));
            } else {
                const result = await createForm.mutateAsync({
                    formId,
                    formData,
                    approvalFlow,
                    language
                });
                toast.success(t('forms:draftSaved'));
                navigate(`/forms/edit/${result.data.applicationId}`, { replace: true });
            }
        } catch (err) {
            toast.error(`${t('forms:editor.saveFailed')}: ${err.message}`);
        }
    };

    // 提交表單
    const handleSubmit = async () => {
        if (!confirm(t('forms:confirmSubmit'))) return;

        try {
            let currentAppId = applicationId;
            
            // 如果是新表單，先創建
            if (!isEditMode) {
                const result = await createForm.mutateAsync({
                    formId,
                    formData,
                    approvalFlow,
                    language
                });
                currentAppId = result.data.applicationId;
            } else {
                // 先保存更新
                await updateForm.mutateAsync({
                    applicationId,
                    data: { formData, approvalFlow }
                });
            }

            // 提交
            await submitForm.mutateAsync(currentAppId);
            toast.success(t('forms:editor.submitSuccess'));
            navigate(`/forms/view/${currentAppId}`);
        } catch (err) {
            toast.error(`${t('forms:editor.submitFailed')}: ${err.message}`);
        }
    };

    // 刪除草稿
    const handleDelete = async () => {
        if (!isEditMode) return;
        if (!confirm(t('forms:confirmDelete'))) return;

        try {
            await deleteForm.mutateAsync(applicationId);
            toast.success(t('forms:editor.draftDeleted'));
            navigate('/forms');
        } catch (err) {
            toast.error(`${t('forms:editor.deleteFailed')}: ${err.message}`);
        }
    };

    // 載入中
    if (configLoading || formLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2">{t('forms:editor.loading')}</span>
            </div>
        );
    }

    // 無配置
    if (!formConfig) {
        return (
            <div className="p-6 text-center text-red-500">
                {t('forms:editor.configNotFound')}
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-4 lg:p-6">
            {/* 頁面標題 */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">
                    {isEditMode ? t('forms:editor.editForm') : t('forms:editor.newForm')}
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                    {getText(formConfig.formName) || formId}
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* 主表單區域 */}
                <div className="lg:col-span-2">
                    <div className="bg-white rounded-lg shadow p-6">
                        <DynamicFormRenderer
                            formConfig={formConfig}
                            formData={formData}
                            onChange={handleChange}
                            errors={errors}
                            readOnly={!canEdit}
                            applicationId={applicationId}
                            formId={currentFormId}
                            onRequireSave={async () => {
                                // Auto-save draft if needed for file upload
                                if (!isEditMode) {
                                    try {
                                        const result = await createForm.mutateAsync({
                                            formId: currentFormId,
                                            formData,
                                            approvalFlow,
                                            language
                                        });
                                        return result.data.applicationId;
                                    } catch (err) {
                                        console.error('Failed to auto-save form:', err);
                                        toast.error(`Failed to save form: ${err.message}`);
                                        throw err;
                                    }
                                }
                                return applicationId;
                            }}
                        />
                    </div>

                    {/* 操作按鈕 */}
                    {canEdit && (
                        <div className="mt-6 flex justify-end gap-3">
                            {isEditMode && (
                                <button
                                    onClick={handleDelete}
                                    disabled={deleteForm.isPending}
                                    className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
                                >
                                    {t('forms:deleteDraft')}
                                </button>
                            )}
                            <button
                                onClick={handleSave}
                                disabled={createForm.isPending || updateForm.isPending}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                                {createForm.isPending || updateForm.isPending ? t('forms:savingDraft') : t('forms:saveDraft')}
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={submitForm.isPending}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >
                                {submitForm.isPending ? t('forms:submitting') : t('forms:submitApplication')}
                            </button>
                        </div>
                    )}
                </div>

                {/* 右側邊欄 - 審批流程編輯器 */}
                <div className="lg:col-span-1 space-y-4">
                    <ApproverFlowEditor
                        approvalFlow={approvalFlow}
                        onChange={setApprovalFlow}
                        flowSettings={routeConfig?.flowSettings || {}}
                        isAdmin={isAdmin}
                        formStatus={formStatus}
                        currentUserId={user?.id}
                        getText={getText}
                    />

                    {/* 管理員特權面板 - 僅在編輯模式下顯示 */}
                    {isEditMode && isAdmin && (
                        <AdminPanel
                            applicationId={applicationId}
                            form={formResult?.data}
                            onActionComplete={() => window.location.reload()}
                            getText={getText}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default FormEditorNew;
